<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRouterStore } from '@/store/router'
import { useSalidaTokens } from '@/composables/useSalidaTokens'
import '@/assets/mayores-theme.css'
import MayoresEncabezado from '@/components/SalidaPublicaMayores/MayoresEncabezado.vue'
import MayoresLista from '@/components/SalidaPublicaMayores/MayoresLista.vue'
import MayoresSalida from '@/components/SalidaPublicaMayores/MayoresSalida.vue'
import MayoresTerritorio from '@/components/SalidaPublicaMayores/MayoresTerritorio.vue'

const route = useRouterStore()
const { fetchPublicSalida, getWsUrl, getMapaMayoresUrl } = useSalidaTokens()

const token = computed(() => route.params.token)

// ─── Preferencias de accesibilidad (persistidas en este navegador) ──────────
const textScale = ref(Number(localStorage.getItem('mayores-text-scale')) || 1)
watch(textScale, (v) => localStorage.setItem('mayores-text-scale', v))

const altoContraste = ref(localStorage.getItem('mayores-alto-contraste') === '1')
watch(altoContraste, (v) => localStorage.setItem('mayores-alto-contraste', v ? '1' : '0'))

// ─── Carga de datos ──────────────────────────────────────────────────────────
const loading = ref(true)
const errorMsg = ref(null)
const data = ref(null)

const getTerrKey = (terr) => `${terr.zona || ''}${terr.numero || ''}`

// ─── Navegación (una sola pantalla visible a la vez) ────────────────────────
// El estado de navegación vive en los parámetros de la URL (salida/territorio)
// para que cada cambio de pantalla quede en el historial del navegador y el
// botón atrás/adelante funcione entre lista, salida y territorio.
const setNavParams = (updates) => {
  const next = { ...route.params }
  for (const [k, v] of Object.entries(updates)) {
    if (v == null) delete next[k]
    else next[k] = v
  }
  route.params = next
}

const activeSalidaIdx = computed(() => {
  const id = route.params.salida
  if (!id || !data.value?.salidas) return null
  const idx = data.value.salidas.findIndex((s) => String(s.id) === String(id))
  return idx > -1 ? idx : null
})
const activeTerrKey = computed(() => route.params.territorio || null)

const pantalla = computed(() => {
  if (activeTerrKey.value) return 'territorio'
  if (activeSalidaIdx.value != null) return 'salida'
  return 'lista'
})

const activeSalida = computed(() => data.value?.salidas?.[activeSalidaIdx.value] || null)

const activeTerritories = computed(() => {
  if (!activeSalida.value || !data.value?.territories) return []
  const keys = activeSalida.value.territorios || []
  return data.value.territories.filter((t) => {
    const key1 = `${t.zona}${t.numero}`
    const key2 = `${t.zona} ${t.numero}`
    const key3 = String(t.numero)
    return keys.some((k) => k === key1 || k === key2 || k === key3 || k === t.zona)
  })
})

const activeTerritorio = computed(() =>
  activeTerritories.value.find((t) => getTerrKey(t) === activeTerrKey.value) || null
)

const captainName = computed(() => {
  const id = activeSalida.value?.conductor
  if (!id) return ''
  return data.value?.config?.conductores?.find((c) => c.id === id)?.name || ''
})

const grupoNombre = computed(() => {
  const grupoIds = activeSalida.value?.grupos
  if (!grupoIds?.length) return ''
  return grupoIds
    .map((gId) => data.value?.config?.grupos?.find((g) => g.id === gId)?.name || gId)
    .join(', ')
})

const ayudaTexto = computed(() => {
  const congregacion = data.value?.config?.congregacion?.name
  if (captainName.value && congregacion) {
    return `Si tenés dudas, consultá con ${captainName.value}, quien organiza esta salida de ${congregacion}.`
  }
  if (captainName.value) return `Si tenés dudas, consultá con ${captainName.value}, quien organiza esta salida.`
  if (congregacion) return `Si tenés dudas, consultá con la congregación ${congregacion}.`
  return 'Si tenés dudas, consultá con la persona que te invitó a esta salida.'
})

const titulo = computed(() => {
  if (pantalla.value === 'lista') return 'Mis salidas'
  if (pantalla.value === 'salida') return 'Salida de predicación'
  if (pantalla.value === 'territorio') return `Territorio ${activeTerrKey.value || ''}`
  return ''
})

const verSalida = (idx) => {
  const salida = data.value?.salidas?.[idx]
  if (!salida) return
  setNavParams({ salida: salida.id, territorio: null })
}

const verTerritorio = (terrKey) => {
  setNavParams({ territorio: terrKey })
}

const volver = () => {
  if (pantalla.value === 'territorio') { setNavParams({ territorio: null }); return }
  if (pantalla.value === 'salida') { setNavParams({ salida: null, territorio: null }); return }
}

// Cambia a la versión normal de SalidaPublica, conservando el token y, si
// había una salida/territorio abiertos, aterrizando ahí mismo en la otra vista.
const irAVistaNormal = () => {
  route.params = {
    ...route.params,
    ...(activeSalida.value ? { salida: activeSalida.value.id } : {}),
    ...(activeTerrKey.value ? { territorio: activeTerrKey.value } : {}),
  }
  route.page = 'salidaPublica'
}

const comoLlegar = (latlng) => {
  if (!latlng) return
  const ll = Array.isArray(latlng) ? latlng : [latlng.lat, latlng.lng]
  window.location.href = `https://www.google.com/maps/dir/?api=1&destination=${ll[0]},${ll[1]}`
}

// ─── Manzanas trabajadas (con actualización en vivo por WebSocket) ──────────
const workedManzanas = ref({})
let wsConn = null
let wsUnmounted = false

const applyManzanaSnapshot = (manzanas) => {
  if (!manzanas || typeof manzanas !== 'object') return
  const next = { ...workedManzanas.value }
  for (const [terrKey, mzList] of Object.entries(manzanas)) {
    if (!Array.isArray(mzList) || !mzList.length) continue
    next[terrKey] = [...new Set([...(next[terrKey] || []), ...mzList.map(Number)])]
  }
  workedManzanas.value = next
}

const applyManzanaUpdate = (terrKey, manzanas) => {
  if (!terrKey || !Array.isArray(manzanas) || !manzanas.length) return
  const current = workedManzanas.value[terrKey] || []
  workedManzanas.value = {
    ...workedManzanas.value,
    [terrKey]: [...new Set([...current, ...manzanas.map(Number)])],
  }
}

const openWs = () => {
  if (wsUnmounted) return
  if (wsConn?.readyState === WebSocket.OPEN || wsConn?.readyState === WebSocket.CONNECTING) return
  wsConn = new WebSocket(getWsUrl(token.value))
  wsConn.onmessage = (ev) => {
    try {
      const msg = JSON.parse(ev.data)
      if (msg.type === 'manzana_snapshot') applyManzanaSnapshot(msg.manzanas)
      else if (msg.type === 'manzana_update') applyManzanaUpdate(msg.terrKey, msg.manzanas)
    } catch { /* noop */ }
  }
  wsConn.onerror = () => {}
  wsConn.onclose = () => { if (!wsUnmounted) setTimeout(openWs, 3000) }
}

const toggleManzana = (idx) => {
  const key = activeTerrKey.value
  if (!key) return
  const current = workedManzanas.value[key] || []
  const yaTrabajada = current.includes(idx)
  workedManzanas.value = {
    ...workedManzanas.value,
    [key]: yaTrabajada ? current.filter((i) => i !== idx) : [...current, idx],
  }
  if (wsConn?.readyState === WebSocket.OPEN) {
    wsConn.send(JSON.stringify({ type: 'manzana_update', terrKey: key, manzanas: workedManzanas.value[key] }))
  }
}

const mapaUrlDeManzana = (idx) => getMapaMayoresUrl(token.value, activeTerrKey.value, idx)

// Abre el WebSocket de actualizaciones en vivo apenas hay una salida activa
// (incluye entrar por navegación interna y volver con el botón atrás/adelante).
watch(activeSalida, (s) => { if (s) openWs() })

onUnmounted(() => {
  wsUnmounted = true
  wsConn?.close()
  wsConn = null
})

// ─── Carga inicial ────────────────────────────────────────────────────────────
onMounted(async () => {
  if (!token.value) {
    errorMsg.value = 'Token no encontrado en la URL.'
    loading.value = false
    return
  }
  try {
    const res = await fetchPublicSalida(token.value)
    if (res.error) {
      errorMsg.value = res.error
    } else {
      // pantalla/activeSalidaIdx/activeTerrKey se derivan de route.params, así
      // que no hace falta más que cargar los datos: si la URL ya trae
      // salida/territorio (enlace directo), la pantalla correcta se muestra sola.
      data.value = res
    }
  } catch {
    errorMsg.value = 'Error al cargar los datos de la salida.'
  }
  loading.value = false
})
</script>

<template>
  <div class="mayores-app" :class="{ 'alto-contraste': altoContraste }" :style="{ '--text-scale': textScale }">
    <MayoresEncabezado
      :titulo="titulo"
      :mostrar-volver="pantalla !== 'lista'"
      :text-scale="textScale"
      :alto-contraste="altoContraste"
      :ayuda-texto="pantalla === 'lista' ? null : ayudaTexto"
      :mostrar-cambiar-vista="pantalla === 'lista'"
      @volver="volver"
      @update:text-scale="textScale = $event"
      @update:alto-contraste="altoContraste = $event"
      @cambiar-vista="irAVistaNormal"
    />

    <div v-if="loading" class="mayores-estado">
      <i class="pi pi-spin pi-spinner mayores-estado__icono" aria-hidden="true" />
      <p>Cargando...</p>
    </div>

    <div v-else-if="errorMsg" class="mayores-estado">
      <i class="pi pi-lock mayores-estado__icono" aria-hidden="true" />
      <p>{{ errorMsg }}</p>
    </div>

    <template v-else>
      <MayoresLista
        v-if="pantalla === 'lista'"
        :salidas="data?.salidas || []"
        :conductores="data?.config?.conductores || []"
        :grupos="data?.config?.grupos || []"
        @ver-salida="verSalida"
      />

      <MayoresSalida
        v-else-if="pantalla === 'salida'"
        :salida="activeSalida"
        :territorios="activeTerritories"
        :get-terr-key="getTerrKey"
        :captain-name="captainName"
        :grupo-nombre="grupoNombre"
        @ver-territorio="verTerritorio"
        @como-llegar="comoLlegar"
      />

      <MayoresTerritorio
        v-else-if="pantalla === 'territorio'"
        :territorio="activeTerritorio"
        :terr-key="activeTerrKey"
        :worked-manzanas="workedManzanas[activeTerrKey] || []"
        :mapa-url-de-manzana="mapaUrlDeManzana"
        @toggle-manzana="toggleManzana"
        @como-llegar="comoLlegar"
      />
    </template>
  </div>
</template>
